<i18n>
{
  "fr": {
    "form-title": "Formulaire d'annonce pour le téléversement de données sur les eaux souterraines",
    "form-follow": "Suivi",
    "form-detail": "Formulaire",
    "logs": "Journal",
    "update-success": "Modifications enregistrées",
    "reminder-sent": "Rappel envoyé",
    "deathline-extended": "Délai prolongé",
    "next-label": "Suivant",
    "start-step-title": "Début",
    "start-description": "Cliquer sur suivant pour notifier que le statut de la demande est en traitement.",
    "openStorage-step-title": "Ouverture de l'espace de stockage",
    "waiting-step-title": "Attente du téléversement",
    "done-step-title": "Fin",
    "done-description": "La demande a été traitée.",
    "user-label": "Utilisateur",
    "pwd-label": "Mot de passe",
    "repository-name-label": "Nom du dépôt",
    "comments-label": "Commentaires",
    "deathline-label": "Délai de la demande",
    "last-reminder-label": "Dernier rappel envoyé",
    "storage-access": "Espace de stockage",
    "status-undefined": "Non défini",
    "status-waiting": "En attente de téléversement",
    "status-cancel": "Annulé",
    "status-running": "En cours",
    "status-done": "Terminé",
    "publicId-label": "N° de référence",
    "createDate-label": "Date de création",
    "lastUpdateDate-label": "Dernière modification",
    "copied-to-clipboard": "Copié dans le presse-papier",
    "get-password": "Récupérer le mot de passe",
    "change-password": "Changer le mot de passe",
    "new-pwd": "Nouveau mot de passe"
  },
  "de": {
    "form-title": "Meldeformular zum Datenupload von Grundwasserdaten",
    "form-follow": "Follow-up",
    "form-detail": "Formular",
    "logs": "Veranstaltung"
  }
}
</i18n>

<template>
  <div id="collect-data-gw" class="geo-forms">

    <Header :title="$t('form-title')" />

    <md-content class="md-layout md-elevation-3 main-container">

      <div class="md-layout-item md-size-25 md-medium-size-35 md-small-size-100">
        <h2>{{ $t('logs') }}</h2>

        <LogForm
          v-if="!isReadOnly"
          v-on:submit="onNewLogSubmit"
          :isDisabled="form.status === 'undefined'"
        />
        <md-divider></md-divider>
        <LogCard v-for="(log, index) in form.logs.logs" :log="log" :key="index" />

      </div>

      <div class="md-layout-item md-size-75 md-small-size-100 md-medium-size-65">
        <md-tabs class="form-tabs" md-sync-route>
          <md-tab id="tab-follow" :md-label="$t('form-follow')" :to="`/form/collect-data-gw/${form.publicId}/follow`"></md-tab>
          <md-tab id="tab-detail" :md-label="$t('form-detail')" :to="`/form/collect-data-gw/${form.publicId}`"></md-tab>
        </md-tabs>

        <md-steppers class="form-steps" md-vertical :md-active-step="activeStep" :md-linear="false">
          <md-step
            id="start"
            :md-label="`${$t('start-step-title')} ${formattedStartDateTitle}`"
            :md-done="isStepDone('start')"
            :md-editable="false"
          >
            <p>{{$t('start-description')}}</p>

            <div class="navigation-step-panel" v-if="!isReadOnly">
              <md-button class="md-raised md-primary" @click="goToStep2">{{ $t('next-label') }}</md-button>
            </div>
          </md-step>

          <md-step
            id="openStorage"
            :md-label="`${$t('openStorage-step-title')} ${formattedOpenStorageDateTitle}`"
            :md-done="isStepDone('openStorage')"
            :md-editable="true"
            v-if="false"
          >

            <div class="md-layout">
              <div class="md-layout-item md-size-50">
                <md-field :class="getValidationClass('user')">
                  <label>{{ $t('user-label') }}</label>
                  <md-input v-model="form.followData.storageData.user" required :disabled="isReadOnly"></md-input>
                </md-field>
                <md-field v-if="formattedOpenStorageDateTitle === ''" :class="getValidationClass('pwd')">
                  <label>{{ $t('pwd-label') }}</label>
                  <md-input v-model="form.followData.storageData.pwd" required type="password" :disabled="isReadOnly"></md-input>
                </md-field>
                <md-field :class="getValidationClass('repositoryName')">
                  <label>{{ $t('repository-name-label') }}</label>
                  <md-input v-model="form.followData.storageData.repositoryName" required :disabled="isReadOnly"></md-input>
                </md-field>
              </div>
              <div class="md-layout-item md-size-50">
                <md-field :class="getValidationClass('comments')">
                  <label>{{ $t('comments-label') }}</label>
                  <md-textarea v-model="form.followData.storageData.comments" md-autogrow :disabled="isReadOnly"></md-textarea>
                </md-field>
              </div>
            </div>

            <md-button v-if="formattedOpenStorageDateTitle !== '' && !isReadOnly" class="md-button md-default md-raised" @click="getStoragePassword">
              {{$t('get-password')}}
            </md-button>
            <md-button v-if="formattedOpenStorageDateTitle !== '' && !isReadOnly" class="md-button md-default md-raised" @click="showChangePwdModal = true">
              {{$t('change-password')}}
            </md-button>

            <div class="navigation-step-panel" v-if="this.formattedOpenStorageDateTitle === '' && !isReadOnly">
              <md-button class="md-raised md-primary" @click="goToStep3">{{ $t('next-label') }}</md-button>
            </div>
          </md-step>

          <md-step
            id="waiting"
            :md-label="`${$t('waiting-step-title')} ${formattedWaitingDateTitle}`"
            :md-done="isStepDone('waiting')"
            :md-editable="true"
          >

            <md-list class="waiting-panel">
              <md-list-item>
                <div v-if="form.isLate()" class="red-dot"></div>
                <span class="md-list-item-text"><b>{{$t('deathline-label')}}</b> {{formattedDeathline}}</span>
                <md-button v-if="!isReadOnly" class="md-icon-button md-list-action md-primary" @click="extendDeathline">
                  <md-icon>autorenew</md-icon>
                </md-button>
              </md-list-item>

              <md-list-item>
                <span class="md-list-item-text"><b>{{$t('last-reminder-label')}}</b> {{formattedLastReminderDate}}</span>
                <md-button v-if="!isReadOnly" class="md-icon-button md-list-action md-primary" @click="sendReminder" :disabled="isSendReminderDisabled">
                  <md-icon>send</md-icon>
                </md-button>
              </md-list-item>

              <md-list-item>
                <md-button class="md-button md-default md-raised" @click="openStorage">
                  {{$t('storage-access')}}
                </md-button>
              </md-list-item>
            </md-list>

            <div class="navigation-step-panel" v-if="this.formattedWaitingDateTitle === '' && !isReadOnly">
              <md-button class="md-raised md-primary" @click="goToStep4">{{ $t('next-label') }}</md-button>
            </div>
          </md-step>

          <md-step
            id="done"
            :md-label="$t('done-step-title')"
            :md-done="isStepDone('done')"
            :md-editable="false"
          >
            <p>{{ $t('done-description') }}</p>

            <md-button class="md-button md-primary md-raised" @click="openStorage">
              {{$t('storage-access')}}
            </md-button>

          </md-step>

        </md-steppers>

      </div>

      <div class="option-panel fixed md-layout md-size-100">
        <div class="align-left md-layout-item md-size-100">
          <md-field class="info">
            <label>{{ $t('publicId-label') }}</label>
            <md-input v-bind:value="form.publicId" disabled></md-input>
          </md-field>
          <md-chip :class="`status-info ${form.status}`">{{ $t('status-' + form.status) }}</md-chip>
          <md-field class="info">
            <label>{{ $t('createDate-label') }}</label>
            <md-input v-bind:value="form.createDate" disabled></md-input>
          </md-field>
          <md-field class="info">
            <label>{{ $t('lastUpdateDate-label') }}</label>
            <md-input v-bind:value="form.lastUpdateDate" disabled></md-input>
          </md-field>
        </div>
      </div>

    </md-content>

    <md-dialog :md-active.sync="showChangePwdModal">
      <md-dialog-title>{{ $t('new-pwd') }}</md-dialog-title>

      <div class="md-layout-item md-size-100">
        <md-field :class="getValidationClass('pwd')">
          <label>{{ $t('pwd-label') }}</label>
          <md-input v-model="form.followData.storageData.pwd" required type="password"></md-input>
        </md-field>
      </div>

      <md-dialog-actions>
        <md-button class="md-primary" @click="showChangePwdModal = false">{{ $t('close') }}</md-button>
        <md-button class="md-primary" @click="changeStoragePwd">{{ $t('save') }}</md-button>
      </md-dialog-actions>
    </md-dialog>

    <Snackbar ref="snackbar" />

  </div>
</template>

<script>
import moment from 'moment'

import FormCollectDataGW from '@/models/FormCollectDataGW/Form'

import AccessToken from '@/helpers/access-token'
import DBEntryLogs from '@/helpers/DBEntryLogs'
import tool from '@/helpers/tool'

import LogCard from '@/components/log-card'
import LogForm from '@/components/log-form'
import Header from '@/components/header'
import Snackbar from '@/components/snackbar'

export default {
  name: 'FormCollectDataGW',
  data () {
    return {
      form: new FormCollectDataGW(),
      contacts: [],
      projects: [],
      stepState: {
        start: {
          isActive: false,
          hasError: null
        },
        openStorage: {
          isActive: false,
          hasError: null
        },
        waiting: {
          isActive: false,
          hasError: null
        },
        done: {
          isActive: false,
          hasError: null
        }
      },
      showChangePwdModal: false
    }
  },
  components: {
    LogCard,
    LogForm,
    Header,
    Snackbar
  },
  mounted () {
    this.$nextTick(() => {
      this.loadData().then(() => {

      }).catch(this.handleError)
    })
  },
  computed: {
    isReadOnly () {
      let accessToken = AccessToken.get()
      return accessToken === null || accessToken.hasReadOnlyrRights()
    },
    activeStep () {
      switch (this.form.status) {
        case 'undefined':
          return 'start'
        case 'running':
          return 'openStorage'
        default:
          return this.form.status
      }
    },
    formattedDeathline () {
      return moment(this.form.deathline).format('DD.MM.YYYY')
    },
    lastReminderDate () {
      let logToShow = this.form.logs.getLastLog(DBEntryLogs.type.REMINDER)

      return logToShow === null ? null : logToShow.date
    },
    formattedLastReminderDate () {
      return this.lastReminderDate === null ? '-' : moment(this.lastReminderDate).format('DD.MM.YYYY')
    },
    isSendReminderDisabled () {
      return moment().diff(this.lastReminderDate, 'days') < 1
    },
    formattedStartDateTitle () {
      let logToShow = this.form.logs.getLogs(DBEntryLogs.type.STATUS_CHANGE).find((log) => {
        return log.to === 'running'
      })

      return typeof logToShow === 'undefined' ? '' : `(${moment(logToShow.date).format('DD.MM.YYYY')})`
    },
    formattedOpenStorageDateTitle () {
      let logToShow = this.form.logs.getLogs(DBEntryLogs.type.STATUS_CHANGE).find((log) => {
        return log.to === 'waiting'
      })

      return typeof logToShow === 'undefined' ? '' : `(${moment(logToShow.date).format('DD.MM.YYYY')})`
    },
    formattedWaitingDateTitle () {
      let logToShow = this.form.logs.getLogs(DBEntryLogs.type.STATUS_CHANGE).find((log) => {
        return log.to === 'done'
      })

      return typeof logToShow === 'undefined' ? '' : `(${moment(logToShow.date).format('DD.MM.YYYY')})`
    }
  },
  methods: {
    getValidationClass (rootName, fieldName) {
      return {
        'md-invalid': false
      }

      /*
      let field

      if (typeof fieldName === 'undefined') {
        field = this.$v.form[rootName]
      } else {
        field = this.$v.form[rootName][fieldName]
      }

      if (field) {
        return {
          'md-invalid': field.$invalid && field.$dirty
        }
      }
      */
    },
    loadData () {
      return new Promise((resolve, reject) => {
        FormCollectDataGW.get(this.$route.params.id).then((form) => {
          this.form = form
          resolve()
        }).catch(reject)
      })
    },
    isLate () {
      return true
    },
    isStepDone (step) {
      switch (step) {
        case 'start':
          return this.form.status !== 'undefined'
        case 'openStorage':
          return this.form.status === 'done'
          // return this.form.status !== 'undefined' && this.form.status !== 'running'
        case 'waiting':
          return this.form.status === 'done'
          // return this.form.status !== 'undefined' && this.form.status !== 'running' && this.form.status !== 'waiting'
        case 'done':
          return this.form.status === 'done'
        default:
          return false
      }
    },
    goToStep2 () {
      this.form.validateStep('start').then((form) => {
        this.form = form
        this.$refs.snackbar.show(this.$t('update-success'))
      }).catch(this.handleError)
    },
    goToStep3 () {
      this.form.validateStep('openStorage').then((form) => {
        this.form = form
        this.$refs.snackbar.show(this.$t('update-success'))
      }).catch(this.handleError)
    },
    goToStep4 () {
      this.form.validateStep('waiting').then((form) => {
        this.form = form
        this.$refs.snackbar.show(this.$t('update-success'))
      }).catch(this.handleError)
    },
    extendDeathline () {
      this.form.extendDeathline().then((form) => {
        this.form = form
        this.$refs.snackbar.show(this.$t('deathline-extended'))
      })
    },
    sendReminder () {
      this.form.sendReminder().then((form) => {
        this.form = form
        this.$refs.snackbar.show(this.$t('reminder-sent'))
      })
    },
    onNewLogSubmit (log) {
      this.form.addLog(log).then(() => {
        this.$refs.snackbar.show(this.$t('update-success'))
      }).catch(this.handleError)
    },
    changeStatus (status) {
      this.form.changeStatus(status).then(() => {
        this.$refs.snackbar.show(this.$t('update-success'))
      }).catch(this.handleError)
    },
    openStorage () {
      window.open('https://swiss-sharing.oodrive.com/workspace/etatduvalais/?lang=FR', '_blank')
    },
    getStoragePassword () {
      this.form.getStoragePassword().then((pwd) => {
        tool.copyToClipboard(pwd)
        this.$refs.snackbar.show(this.$t('copied-to-clipboard'))
      }).catch(this.handleError)
    },
    changeStoragePwd () {
      this.form.validateStep('openStorage').then((form) => {
        this.form = form
        this.$refs.snackbar.show(this.$t('update-success'))
        this.showChangePwdModal = false
        // this.activeStep = 'waiting'
      }).catch(this.handleError)
    },
    handleError (e) {
      this.$refs.snackbar.showError(e)
    }
  }
}
</script>

<style lang="scss">

.option-panel {
  z-index: 10;
}

.flex-container {
  display: flex;
}

.group-field {
  padding: 20px;

  &.narrow {
    .md-layout-item {
      padding: 7px;
      margin: 5px;
    }
  }
}

.data-form.section {
  padding-top: 25px;
  padding-bottom: 35px;

  h3 {
    font-size: 1.5em;
    margin-top: 0px;
  }
}

.md-layout-item {
  padding: 10px;
}

.map {
  width: 100%;
  height:400px;
}

.md-select-menu.md-menu-content-bottom-start.md-menu-content-small.md-menu-content.md-theme-default {
  @media(max-width: 960px) {
    left: 10px !important;
    max-width: calc(100% - 20px);
  }
  @media(min-width: 960px){
    max-width: 50%
  }
}

.navigation-step-panel {
  text-align: right;
}

.change-form-status-menu {
  margin-top: 6px;
}

.form-steps {
  @media(max-width: 600px) {
    padding: 0px !important;
  }
}

.form-tabs {
  margin-bottom: 12px;
}

@media(min-width: 1260px) {
  .md-layout-item.md-size-20 {
    min-width: 19% !important;
    max-width: 19% !important;
    flex: 0 1 19% !important;
  }
}

.waiting-panel {
  width: 300px;
  margin-top: 10px !important;
}

.red-dot {
  background-color: red;
  width: 8px;
  height: 8px;
  display: inline-block;
  border-radius: 8px;
  margin-right: 20px;
}

</style>
